import { useState, useEffect, useMemo, useRef } from "react";
import { Box, Checkbox, Chip } from "@mui/material";
import TableComponent, { IColumnProps } from "../../../components/TableComponent";
import { IPaginationParams } from "../../../services/types";
import { t } from "i18next";
import { useDebounce } from "../../../services/SchoolTransportation/hooks/useDebounce";
import { useLocations } from "./LocationContext";
import { ILocationProps } from "../../../services/Location/types";

interface LocationListProps {
    onSelect: (id: string) => void;
    selectedIds: string[];
    setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
}

type ConnectionFilter = "all" | "online" | "offline";

function resolveVehicleConnectionStatus(location: ILocationProps): "online" | "offline" {
    if (typeof location.isOnline === "boolean") {
        return location.isOnline ? "online" : "offline";
    }

    if (!location.timestamp) {
        return "offline";
    }

    const lastTimestamp = new Date(location.timestamp).getTime();
    if (!Number.isFinite(lastTimestamp)) {
        return "offline";
    }

    return Date.now() - lastTimestamp <= 5 * 60 * 1000 ? "online" : "offline";
}

function splitAddressAfterCity(address?: string) {
    const normalized = address?.trim();

    if (!normalized) {
        return { firstLine: "Buscando endereço...", secondLine: null as string | null };
    }

    const citySeparatorIndex = normalized.indexOf(" - ");
    if (citySeparatorIndex < 0) {
        return { firstLine: normalized, secondLine: null as string | null };
    }

    return {
        firstLine: normalized.slice(0, citySeparatorIndex),
        secondLine: normalized.slice(citySeparatorIndex + 3),
    };
}

function getPlatePresentation(plate?: string) {
    const normalized = plate?.trim();

    if (!normalized) {
        return { display: "-", isOldModel: false };
    }

    const upperText = normalized.toUpperCase();
    const plateMatch = upperText.match(/([A-Z]{3})[\s-]?([A-Z0-9]{4})/);

    if (plateMatch) {
        const base = `${plateMatch[1]}${plateMatch[2]}`;
        const fifthChar = base[4];
        const isOldModel = /\d/.test(fifthChar);

        return {
            display: `${base.slice(0, 3)}-${base.slice(3, 7)}`,
            isOldModel,
        };
    }

    const alphanumeric = upperText.replace(/[^A-Z0-9]/g, "");

    if (!alphanumeric) {
        return { display: "-", isOldModel: false };
    }

    const base = alphanumeric.slice(0, 7);

    if (base.length <= 3) {
        return { display: base, isOldModel: false };
    }

    const isOldModel = base.length >= 5 && /\d/.test(base[4]);

    return {
        display: `${base.slice(0, 3)}-${base.slice(3)}`,
        isOldModel,
    };
}

const LocationList = ({ onSelect, selectedIds, setSelectedIds }: LocationListProps) => {
    const [pagination, setPagination] = useState<IPaginationParams>({
        pageNumber: 1,
        pageSize: 10,
    });

    const [searchTerm, setSearchTerm] = useState("");
    const [connectionFilter, setConnectionFilter] = useState<ConnectionFilter>("all");
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const hasManualSelectionChange = useRef(false);

    const locations = useLocations();
    const locationIds = useMemo(
        () => Array.from(new Set(locations.map(l => l.entityId).filter(Boolean))),
        [locations],
    );

    useEffect(() => {
        if (locationIds.length === 0 || hasManualSelectionChange.current) {
            return;
        }

        setSelectedIds((previousIds) => {
            if (previousIds.length === 0) {
                return locationIds;
            }

            const missingIds = locationIds.filter((id) => !previousIds.includes(id));
            if (missingIds.length === 0) {
                return previousIds;
            }

            return [...previousIds, ...missingIds];
        });
    }, [locationIds, setSelectedIds]);

    const normalizedSearch = debouncedSearchTerm.toLowerCase();

    const filteredLocations = useMemo(() => {
        return locations.filter((loc) => {
            const matchesSearch = !normalizedSearch || [loc.entityId, loc.vehiclePlate, loc.modelName, loc.address]
                .filter(Boolean)
                .some((value) => (value as string).toLowerCase().includes(normalizedSearch));

            if (!matchesSearch) {
                return false;
            }

            if (connectionFilter === "all") {
                return true;
            }

            return resolveVehicleConnectionStatus(loc) === connectionFilter;
        });
    }, [locations, normalizedSearch, connectionFilter]);

    const onlineCount = useMemo(
        () => locations.filter((loc) => resolveVehicleConnectionStatus(loc) === "online").length,
        [locations],
    );

    const offlineCount = useMemo(
        () => locations.filter((loc) => resolveVehicleConnectionStatus(loc) === "offline").length,
        [locations],
    );

    const paginatedLocations = useMemo(() => {
        const startIndex = (pagination.pageNumber - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        return filteredLocations.slice(startIndex, endIndex);
    }, [filteredLocations, pagination.pageNumber, pagination.pageSize]);

    const columns: IColumnProps[] = [
        {
            id: "select",
            label: "",
            width: "56px",
            render: (_value: string, _rowIndex: number, row?: any) => {
                const loc = row as ILocationProps;
                return (
                    <Checkbox
                        size="small"
                        sx={{ p: 0, m: 0 }}
                        checked={selectedIds.includes(loc.entityId)}
                        onChange={(e) => {
                            hasManualSelectionChange.current = true;

                            if (e.target.checked) {
                                setSelectedIds((prev) => prev.includes(loc.entityId) ? prev : [...prev, loc.entityId]);
                            } else {
                                setSelectedIds((prev) => prev.filter(id => id !== loc.entityId));
                            }
                        }}
                    />
                );
            },
        },
        {
            id: "vehiclePlate",
            label: "Placa",
            width: "24%",
            render: (value: string, _rowIndex: number, row?: any) => {
                const { display: formattedPlate, isOldModel } = getPlatePresentation(value);

                return (
                    <span
                        style={{
                            cursor: "pointer",
                            display: "inline-flex",
                            flexDirection: "column",
                            width: 96,
                            border: "2px solid #0f172a",
                            borderRadius: 5,
                            overflow: "hidden",
                            background: isOldModel
                                ? "linear-gradient(180deg, #f3f4f6 0%, #d1d5db 100%)"
                                : "linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)",
                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.35)",
                            lineHeight: 1,
                        }}
                        onClick={() => {
                            const loc = row as ILocationProps;
                            onSelect(loc.entityId);
                        }}
                        title={formattedPlate}
                    >
                        <span
                            style={{
                                display: "block",
                                height: 8,
                                backgroundColor: isOldModel ? "#9ca3af" : "#1d4ed8",
                            }}
                        />
                        <span
                            style={{
                                display: "block",
                                textAlign: "center",
                                color: "#111827",
                                fontSize: 11,
                                fontWeight: 800,
                                letterSpacing: isOldModel ? 1 : 0.8,
                                lineHeight: "14px",
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                padding: "1px 4px",
                            }}
                        >
                            {formattedPlate}
                        </span>
                    </span>
                );
            },
        },
        {
            id: "location",
            label: "Endereço",
            width: "46%",
            render: (_value: string, _rowIndex: number, row?: any) => {
                const loc = row as ILocationProps;
                const text = loc.address?.trim() || "Endereço não informado";
                const addressLines = splitAddressAfterCity(loc.address);

                return (
                    <span
                        title={text}
                        style={{
                            display: "inline-block",
                            maxWidth: "100%",
                            lineHeight: 1.25,
                        }}
                    >
                        <span style={{ display: "block" }}>{addressLines.firstLine}</span>
                        {addressLines.secondLine && <span style={{ display: "block" }}>{addressLines.secondLine}</span>}
                    </span>
                );
            },
        },
        {
            id: "timestamp",
            label: t("map.date"),
            width: "20%",
            render: (value: string | null) => {
                if (!value) {
                    return <span>-</span>;
                }

                const date = new Date(value);
                const dateText = date.toLocaleDateString();
                const timeText = date.toLocaleTimeString();

                return (
                    <span style={{ display: "inline-block", lineHeight: 1.2 }}>
                        <span style={{ display: "block" }}>{dateText}</span>
                        <span style={{ display: "block" }}>{timeText}</span>
                    </span>
                );
            },
        },
    ];

    return (
        <TableComponent
            title={<span style={{ fontWeight: "bold" }}>{t("map.locations")}</span>}
            additionalTitleComponent={(
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                        size="small"
                        label={`Todos (${locations.length})`}
                        color={connectionFilter === "all" ? "primary" : "default"}
                        onClick={() => setConnectionFilter("all")}
                    />
                    <Chip
                        size="small"
                        label={`Online (${onlineCount})`}
                        color={connectionFilter === "online" ? "success" : "default"}
                        onClick={() => setConnectionFilter("online")}
                    />
                    <Chip
                        size="small"
                        label={`Offline (${offlineCount})`}
                        color={connectionFilter === "offline" ? "error" : "default"}
                        onClick={() => setConnectionFilter("offline")}
                    />
                </Box>
            )}
            rows={paginatedLocations}
            columns={columns}
            isLoading={false}
            totalCount={filteredLocations.length}
            onChangePageOrRowsPerPage={setPagination}
            onChangeMainFilter={setSearchTerm}
            searchValue={searchTerm}
        />
    );
};

export default LocationList;
